'use client';

import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { Drawer as DrawerPrimitive } from 'vaul';

export const Drawer = ({
  shouldScaleBackground = true,
  direction = 'right',
  ...props
}: ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    direction={direction}
    {...props}
  />
);

export const DrawerPortal = (props: ComponentProps<typeof DrawerPrimitive.Portal>) => (
  <DrawerPrimitive.Portal {...props} />
);

export const DrawerClose = (props: ComponentProps<typeof DrawerPrimitive.Close>) => (
  <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
);

// `plain` is an unstyled passthrough trigger (its host wraps it however it
// likes). `button` is the standalone pill — full-width, bordered, inverted —
// used when the trigger IS the visible control (e.g. the mobile filters bar).
const drawerTriggerVariants = cva('', {
  variants: {
    variant: {
      plain: '',
      button: [
        'font-heading',
        'border-foreground flex w-full items-center justify-between rounded-lg border',
        'bg-background-inverted text-foreground-inverted px-6 py-4 text-sm tracking-widest uppercase',
      ],
    },
  },
  defaultVariants: { variant: 'plain' },
});

export type DrawerTriggerProps = ComponentProps<typeof DrawerPrimitive.Trigger> &
  VariantProps<typeof drawerTriggerVariants>;

export const DrawerTrigger = ({ className, variant, ...props }: DrawerTriggerProps) => (
  <DrawerPrimitive.Trigger
    data-slot="drawer-trigger"
    className={clsxm(drawerTriggerVariants({ variant }), className)}
    {...props}
  />
);

export const DrawerOverlay = ({ className, ...props }: ComponentProps<'div'>) => (
  <DrawerPrimitive.Overlay
    data-slot="drawer-overlay"
    className={clsxm('bg-foreground/40 fixed inset-0 z-(--z-max)', className)}
    {...props}
  />
);

// `right` is the side sheet (cart): right-anchored, padded, capped width on
// larger viewports. `bottom` is the bottom sheet (filters): edge-to-edge, top
// corners rounded, capped just below the viewport top. Pair the `side` here
// with vaul's matching `direction` on <Drawer>.
const drawerContentVariants = cva('bg-background-intense fixed z-(--z-max) flex w-full flex-col', {
  variants: {
    side: {
      right: [
        'border-foreground/10 inset-y-0 right-0 h-4/5 border-l',
        'px-content-safe-padding-sm py-4',
        'tab:h-full tab:w-[584px] tab:px-content-safe-padding-lg tab:py-10',
      ],
      bottom: 'inset-x-0 bottom-0 h-full max-h-[calc(100%-64px)] rounded-t-2xl',
    },
  },
  defaultVariants: { side: 'right' },
});

export type DrawerContentProps = ComponentProps<typeof DrawerPrimitive.Content> &
  VariantProps<typeof drawerContentVariants>;

export const DrawerContent = ({ className, children, side, ...props }: DrawerContentProps) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      data-slot="drawer-content"
      className={clsxm(drawerContentVariants({ side }), className)}
      {...props}
    >
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
);

// `scroll` makes the header the drawer's scrollable body (filters list);
// `plain` is the simple stacked header.
const drawerHeaderVariants = cva('flex flex-col', {
  variants: {
    variant: {
      plain: 'tab:text-left gap-1.5 text-center',
      scroll: 'scrollbar-hidden mx-4 mt-6 mb-2 flex-grow-1 gap-2 overflow-y-scroll',
    },
  },
  defaultVariants: { variant: 'plain' },
});

export type DrawerHeaderProps = ComponentProps<'div'> & VariantProps<typeof drawerHeaderVariants>;

export const DrawerHeader = ({ className, variant, ...props }: DrawerHeaderProps) => (
  <div
    data-slot="drawer-header"
    className={clsxm(drawerHeaderVariants({ variant }), className)}
    {...props}
  />
);

// `elevated` is the pinned action bar (its own surface + top drop-shadow);
// `plain` is the simple stacked footer.
const drawerFooterVariants = cva('flex flex-col gap-2', {
  variants: {
    variant: {
      plain: 'mt-0',
      elevated: 'bg-background drop-shadow-foreground/50 justify-end p-4 drop-shadow-2xl',
    },
  },
  defaultVariants: { variant: 'plain' },
});

export type DrawerFooterProps = ComponentProps<'div'> & VariantProps<typeof drawerFooterVariants>;

export const DrawerFooter = ({ className, variant, ...props }: DrawerFooterProps) => (
  <div
    data-slot="drawer-footer"
    className={clsxm(drawerFooterVariants({ variant }), className)}
    {...props}
  />
);

// `heading` is the brand-font title row that also lays out an inline close
// affordance (filters); `plain` is the simple semibold title.
const drawerTitleVariants = cva('text-lg leading-none tracking-tight', {
  variants: {
    variant: {
      plain: 'font-semibold',
      heading: 'font-heading flex h-min justify-between gap-4',
    },
  },
  defaultVariants: { variant: 'plain' },
});

export type DrawerTitleProps = ComponentProps<typeof DrawerPrimitive.Title> &
  VariantProps<typeof drawerTitleVariants>;

export const DrawerTitle = ({ className, variant, ...props }: DrawerTitleProps) => (
  <DrawerPrimitive.Title
    data-slot="drawer-title"
    className={clsxm(drawerTitleVariants({ variant }), className)}
    {...props}
  />
);

export const DrawerDescription = ({
  className,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Description>) => (
  <DrawerPrimitive.Description
    data-slot="drawer-description"
    className={clsxm('text-foreground-muted text-sm', className)}
    {...props}
  />
);

export {
  drawerContentVariants,
  drawerFooterVariants,
  drawerHeaderVariants,
  drawerTitleVariants,
  drawerTriggerVariants,
};
